import { defineStore } from 'pinia'

// Auth via httpOnly-cookie: de JWT staat NIET meer in localStorage (niet leesbaar
// voor JS, dus veilig tegen XSS-diefstal). De browser stuurt de sessie-cookie
// automatisch mee bij same-origin requests. In localStorage bewaren we alleen het
// niet-gevoelige user-profiel (naam/e-mail) voor een flitsvrije UI; de echte
// waarheid over "ingelogd?" komt van /api/auth/me.
const USER_KEY = 'p2c_user'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,       // { id, email, firstName, lastName, displayName }
    loading: false,
    error: null,
    checked: false,   // is de sessie al één keer bij de server geverifieerd?
  }),
  getters: {
    isLoggedIn:  (state) => !!state.user,
    currentUser: (state) => state.user,
    userName:    (state) => state.user ? `${state.user.firstName} ${state.user.lastName}`.trim() : '',
  },
  actions: {
    _persist() {
      if (typeof window === 'undefined') return
      if (this.user) localStorage.setItem(USER_KEY, JSON.stringify(this.user))
      else localStorage.removeItem(USER_KEY)
    },

    // Init (client only): toon direct het gecachete profiel, valideer daarna de
    // cookie-sessie bij de server (fire-and-forget).
    init() {
      if (import.meta.env.SSR || typeof window === 'undefined') return
      // Opruimen van de oude localStorage-token uit de vorige (onveilige) opzet.
      localStorage.removeItem('p2c_token')
      const cached = localStorage.getItem(USER_KEY)
      if (cached) { try { this.user = JSON.parse(cached) } catch {} }
      this.checkSession()
    },

    // Valideert de httpOnly-cookie bij de server. Geldig → user (ver)zetten.
    // Ongeldig/geen sessie → lokaal uitloggen.
    async checkSession() {
      if (typeof window === 'undefined') return
      try {
        const res = await fetch('/api/auth/me', { credentials: 'same-origin' })
        if (res.ok) {
          const data = await res.json()
          this.user = data.user
          this._persist()
        } else if (res.status === 401) {
          this.user = null
          this._persist()
        }
        // andere statussen (5xx): laat het gecachete profiel staan
      } catch { /* netwerkfout: gecachet profiel behouden */ }
      finally { this.checked = true }
    },

    async login(email, password) {
      this.loading = true
      this.error = null
      try {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ email, password })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.message || 'Inloggen mislukt')
        this.user = data.user
        this.checked = true
        this._persist()
        return true
      } catch (err) {
        this.error = err.message
        throw err
      } finally {
        this.loading = false
      }
    },

    async register(firstName, lastName, email, password) {
      this.loading = true
      this.error = null
      try {
        const res = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ firstName, lastName, email, password })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.message || 'Registratie mislukt')
        // Auto-login lukte niet server-side → alsnog inloggen (zet de cookie).
        if (data.requiresLogin) {
          await this.login(email, password)
        } else {
          this.user = data.user
          this.checked = true
          this._persist()
        }
        return true
      } catch (err) {
        this.error = err.message
        throw err
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
      } catch { /* cookie kan lokaal al weg zijn; negeer netwerkfout */ }
      this.user = null
      this.error = null
      this._persist()
    },

    async fetchOrders() {
      if (!this.isLoggedIn) return []
      try {
        const res = await fetch('/api/auth/orders', { credentials: 'same-origin' })
        if (res.status === 401) { this.user = null; this._persist(); return [] }
        if (!res.ok) throw new Error('Kon bestellingen niet ophalen')
        return await res.json()
      } catch { return [] }
    },

    async updateProfile(data) {
      if (!this.user) throw new Error('Niet ingelogd')
      this.loading = true
      try {
        const res = await fetch('/api/auth/profile', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(data)
        })
        const result = await res.json()
        if (!res.ok) throw new Error(result.message || 'Bijwerken mislukt')
        this.user = { ...this.user, ...result.user }
        this._persist()
        return true
      } catch (err) {
        this.error = err.message
        throw err
      } finally {
        this.loading = false
      }
    }
  }
})
